import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import { SANS_241_TABLES, WHO_TABLES, REFERENCE_META } from "../lib/content/compliance-parameters";

/**
 * /compliance/sans-241-parameters
 * /compliance/who-parameters
 *
 * Formal reference tables for the two water-quality benchmarks named on the
 * Compliance page. Same template as the rest of the marketing site
 * (HidroVerse light theme, navy + cyan, mono eyebrows).
 *
 * The values reproduced here are the numerical determinand limits published
 * in SANS 241:2015 and the WHO Guidelines for Drinking-water Quality (4th
 * edition, incorporating the 1st and 2nd addenda). The primary sources are
 * cited at the top of each page and linked at the bottom.
 */

const KIND_MAP = {
  "sans-241-parameters": {
    key: "sans241",
    tables: SANS_241_TABLES,
  },
  "who-parameters": {
    key: "who",
    tables: WHO_TABLES,
  },
};

export default function ComplianceParameterReference() {
  const { slug } = useParams();
  const kind = KIND_MAP[slug];
  if (!kind) {
    return (
      <div className="px-6 md:px-12 lg:px-20 py-24 text-center">
        <h1 className="font-display" style={{ fontSize: "clamp(1.6rem, 3vw, 2.4rem)", color: "#0C2A40" }}>
          Reference not found.
        </h1>
        <Link to="/compliance" className="mt-6 inline-flex items-center gap-2 font-mono uppercase" style={{ fontSize: "0.7rem", letterSpacing: "0.18em", color: "#1FB6C1" }}>
          <ArrowLeft size={12} aria-hidden /> Back to compliance
        </Link>
      </div>
    );
  }
  const meta = REFERENCE_META[kind.key];
  return (
    <div data-testid={`ref-${slug}`}>
      <SEO title={meta.seoTitle} description={meta.seoDescription} path={`/compliance/${slug}`} />

      {/* HERO */}
      <section
        className="px-6 md:px-12 lg:px-20 pt-32 pb-14"
        style={{ background: "#0C2A40", color: "#F5F7FA" }}
        data-testid="ref-hero"
      >
        <div className="max-w-[1300px] mx-auto">
          <Link
            to="/compliance"
            className="inline-flex items-center gap-2 font-mono uppercase mb-6"
            style={{ fontSize: "0.66rem", letterSpacing: "0.2em", color: "rgba(245,247,250,0.65)" }}
          >
            <ArrowLeft size={12} aria-hidden /> Back to compliance
          </Link>
          <div
            className="mb-4 inline-flex items-center gap-2 px-3 py-1.5"
            style={{
              background: "rgba(31,182,193,0.14)",
              border: "1px solid rgba(31,182,193,0.35)",
              color: "#1FB6C1",
              fontSize: "0.64rem", letterSpacing: "0.22em",
              textTransform: "uppercase", fontWeight: 700, borderRadius: 999,
            }}
          >
            Reference · {meta.eyebrow}
          </div>
          <h1
            className="font-display"
            style={{
              fontSize: "clamp(2rem, 4.6vw, 3.6rem)",
              lineHeight: 1.05,
              letterSpacing: "-0.02em",
              fontWeight: 400,
              color: "#F5F7FA",
              maxWidth: "22ch",
            }}
          >
            {meta.title}
          </h1>
          <p className="mt-6 max-w-3xl font-light leading-relaxed" style={{ fontSize: "1rem", color: "rgba(245,247,250,0.78)" }}>
            {meta.lede}
          </p>
          <p className="mt-4 font-mono uppercase" style={{ fontSize: "0.66rem", letterSpacing: "0.2em", color: "rgba(245,247,250,0.55)" }}>
            {meta.provenance}
          </p>
        </div>
      </section>

      {/* TABLES */}
      <section className="px-6 md:px-12 lg:px-20 py-14 md:py-20" style={{ background: "#FFFFFF" }} data-testid="ref-tables">
        <div className="max-w-[1300px] mx-auto space-y-14">
          {kind.tables.map((t) => (
            <div key={t.name} data-testid={`ref-table-${t.name.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="mb-6">
                <div className="font-mono uppercase mb-2" style={{ fontSize: "0.62rem", letterSpacing: "0.22em", color: "#1FB6C1", fontWeight: 700 }}>
                  {t.eyebrow}
                </div>
                <h2 className="font-display" style={{ fontSize: "clamp(1.4rem, 2.6vw, 2rem)", lineHeight: 1.15, letterSpacing: "-0.012em", fontWeight: 600, color: "#0C2A40" }}>
                  {t.name}
                </h2>
                {t.notes && (
                  <p className="mt-3 font-light leading-relaxed max-w-3xl" style={{ fontSize: "0.94rem", color: "rgba(26,31,37,0.72)" }}>
                    {t.notes}
                  </p>
                )}
              </div>
              <div
                className="rounded-md overflow-hidden"
                style={{
                  background: "#FFFFFF",
                  border: "1px solid rgba(12,42,64,0.10)",
                  boxShadow: "0 12px 30px rgba(12,42,64,0.06)",
                }}
              >
                <div className="overflow-x-auto">
                  <table className="w-full text-left" style={{ fontSize: "0.9rem", minWidth: 640 }}>
                    <thead>
                      <tr style={{ background: "#F5F5F0", color: "rgba(12,42,64,0.7)" }}>
                        {t.columns.map((c) => (
                          <th
                            key={c}
                            className="px-4 py-3 font-mono uppercase"
                            style={{ fontSize: "0.6rem", letterSpacing: "0.18em" }}
                          >
                            {c}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {t.rows.map((r, i) => (
                        <tr key={i} style={{ borderTop: "1px solid rgba(12,42,64,0.06)" }}>
                          {r.map((cell, j) => (
                            <td
                              key={j}
                              className={j === 0 ? "px-4 py-2.5 font-display" : "px-4 py-2.5"}
                              style={{
                                color: j === 0 ? "#0C2A40" : "rgba(26,31,37,0.78)",
                                fontWeight: j === 0 ? 600 : 400,
                                fontSize: j === 0 ? "0.92rem" : "0.88rem",
                              }}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SOURCES */}
      <section className="px-6 md:px-12 lg:px-20 py-14 md:py-20" style={{ background: "#FAFAF7" }} data-testid="ref-sources">
        <div className="max-w-[1300px] mx-auto">
          <h2 className="font-display mb-6" style={{ fontSize: "clamp(1.3rem, 2.4vw, 1.8rem)", lineHeight: 1.15, letterSpacing: "-0.01em", fontWeight: 500, color: "#0C2A40" }}>
            Primary sources.
          </h2>
          <ul className="space-y-3">
            {meta.sources.map((s) => (
              <li key={s.url}>
                <a
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 hover:underline"
                  style={{ fontSize: "0.95rem", color: "#0C2A40" }}
                >
                  <span className="font-mono uppercase" style={{ fontSize: "0.6rem", letterSpacing: "0.16em", color: "#1FB6C1", fontWeight: 700 }}>
                    Source
                  </span>
                  <span className="font-light" style={{ color: "rgba(26,31,37,0.78)" }}>{s.label}</span>
                  <ArrowRight size={12} aria-hidden style={{ color: "#1FB6C1" }} />
                </a>
              </li>
            ))}
          </ul>
          <div className="mt-10 pt-6" style={{ borderTop: "1px solid rgba(12,42,64,0.08)" }}>
            <p className="font-light" style={{ fontSize: "0.82rem", color: "rgba(26,31,37,0.6)", maxWidth: "70ch" }}>
              {meta.disclaimer}
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-strip">
        <div className="csi">
          <div>
            <h3>Need this designed and documented on your site?</h3>
            <p className="csi-meta">Contact HidroVerse for a compliance-first water-treatment design.</p>
          </div>
          <Link to="/contact" className="hv-magnetic" style={{ justifyContent: "center" }}>
            Contact us <span aria-hidden>→</span>
          </Link>
        </div>
      </section>
    </div>
  );
}
